import * as Sentry from '@sentry/browser';
import queryString, { ParsedQuery } from 'query-string';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { openOrCloseGlobalDialog } from '../action/dialog/global-dialog';

import { GLOBAL_DIALOGS } from '../dialogs/global-dialogs-config';

import { QUICKBOOKS } from '../config/quickbooks-config';

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  actions: {
    openQuickbooksConfirmationDialog: (context: QuickbooksIntegrationAuthResponse) =>
      dispatch(
        openOrCloseGlobalDialog(GLOBAL_DIALOGS.QUICKBOOKS_CONFIRMATION_DIALOG.key, true, context),
      ),
  },
});

type InputProps = {
  queryParams: ParsedQuery;
};

type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & DispatchProps & RouteComponentProps;

export const QuickbooksAuthenticationRedirect = withRouter(
  connect(
    null,
    mapDispatchToProps,
  )((props: Props) => {
    // IF (12/30/20): It's worth noting that the type of ParsedQuery = string | string[] | undefined |
    // null so that's why we're doing all the array checks here

    const openDialogAndRedirect = (
      redirectUrl: string,
      context: QuickbooksIntegrationAuthResponse,
    ) => {
      props.actions.openQuickbooksConfirmationDialog(context);
      props.history.replace(redirectUrl);
    };

    const currentUrl = window.location.href;
    const { error, state, code, realmId } = props.queryParams;

    if (!state || Array.isArray(state)) {
      Sentry.captureMessage(`Quickbooks Integration: missing or malformed state (${currentUrl})`);
      openDialogAndRedirect('/', { error: true });
      return null;
    }

    const { xssSecurityToken, orgId, redirect } = queryString.parse(state);

    if (!redirect || Array.isArray(redirect)) {
      Sentry.captureMessage(
        `Quickbooks Integration: missing or malformed redirect (${currentUrl})`,
      );
      openDialogAndRedirect('/', { error: true });
      return null;
    }

    if (
      !xssSecurityToken ||
      Array.isArray(xssSecurityToken) ||
      xssSecurityToken !== QUICKBOOKS.XSS_SECURITY_TOKEN
    ) {
      Sentry.captureMessage(`Quickbooks Integration: invalid security token (${currentUrl})`);
      openDialogAndRedirect(redirect, { error: true });
      return null;
    }

    if (error) {
      // IF (12/30/20): In the case of the user just denying us access to their quickbooks, there's no
      // reason to show them a dialog - just return them to the page they were on
      if (error === 'access_denied') {
        props.history.replace(redirect);
      } else {
        Sentry.captureMessage(
          `Quickbooks Integration: responded with error ${error} (${currentUrl})`,
        );
        openDialogAndRedirect(redirect, { error: true });
      }
      return null;
    }

    if (
      !code ||
      !realmId ||
      !orgId ||
      Array.isArray(code) ||
      Array.isArray(realmId) ||
      Array.isArray(orgId)
    ) {
      Sentry.captureMessage(`Quickbooks Integration: missing or malformed params (${currentUrl})`);
      openDialogAndRedirect(redirect, { error: true });
      return null;
    }

    openDialogAndRedirect(redirect, { code, realmId, orgId });
    return null;
  }),
);
